import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from "recharts";
import {
  Briefcase,
  AlertTriangle,
  Users,
  TrendingUp,
  Activity,
  ChevronRight,
  CheckCircle,
  Clock,
} from "lucide-react";
import { useAuth } from "../hooks/useAuth";
import { get } from "../api/client";
import { AuditEntry } from "../types";
import KPICard from "../components/KPICard";
import VacatureListModal from "../components/VacatureListModal";

// ── Types ──────────────────────────────────────────────────────────────────────

interface HMPActivity {
  id: number;
  name: string;
  email: string;
  last_login: string | null;
  days_since_login: number | null;
  vacatures_added_30d: number;
  vacatures_updated_30d: number;
  stale_vacatures: number;
  total_vacatures: number;
}

interface PerVestiging {
  tab_name: string;
  open: number;
  hoog: number;
  in_bemiddeling: number;
}

interface PartnerAdminDashboardData {
  partner_name: string;
  totaal_vacatures: number;
  open_vacatures: number;
  hoge_prioriteit: number;
  in_bemiddeling: number;
  geplaatst: number;
  hmp_activity: HMPActivity[];
  per_vestiging: PerVestiging[];
  recent_activity: AuditEntry[];
}

// ── Helpers ────────────────────────────────────────────────────────────────────

function LoadingState() {
  return (
    <div className="flex items-center justify-center py-24">
      <div className="w-8 h-8 border-2 border-waves-blue border-t-transparent rounded-full animate-spin" />
    </div>
  );
}

function ErrorState({ message }: { message: string }) {
  return (
    <div className="bg-red-50 border border-red-200 text-red-700 rounded-xl p-6 text-sm">
      <strong>Fout bij laden:</strong> {message}
    </div>
  );
}

function actionLabel(action: string): string {
  const map: Record<string, string> = {
    create: "Aangemaakt",
    update: "Bijgewerkt",
    delete: "Verwijderd",
    status_change: "Status gewijzigd",
    login: "Ingelogd",
  };
  return map[action] ?? action;
}

function actionBadge(action: string) {
  const styles: Record<string, string> = {
    create: "bg-green-100 text-green-700",
    update: "bg-blue-100 text-blue-700",
    delete: "bg-red-100 text-red-600",
    status_change: "bg-yellow-100 text-yellow-700",
    login: "bg-slate-100 text-slate-600",
  };
  return (
    <span className={`pill ${styles[action] ?? "bg-slate-100 text-slate-600"}`}>
      {actionLabel(action)}
    </span>
  );
}

// ── Clickable KPI Card wrapper ─────────────────────────────────────────────────

interface ClickableKPICardProps {
  children: React.ReactNode;
  onClick: () => void;
}

function ClickableKPICard({ children, onClick }: ClickableKPICardProps) {
  return (
    <div
      onClick={onClick}
      className="cursor-pointer group focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-waves-blue rounded-2xl"
      role="button"
      tabIndex={0}
      onKeyDown={(e) => e.key === "Enter" && onClick()}
      style={{ transition: "transform 0.12s ease" }}
      onMouseEnter={(e) =>
        ((e.currentTarget as HTMLDivElement).style.transform = "translateY(-2px)")
      }
      onMouseLeave={(e) =>
        ((e.currentTarget as HTMLDivElement).style.transform = "translateY(0)")
      }
    >
      {children}
    </div>
  );
}

// ── Login Badge ────────────────────────────────────────────────────────────────

function LoginBadge({ daysSinceLogin }: { daysSinceLogin: number | null }) {
  if (daysSinceLogin === null) {
    return (
      <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-700">
        Nooit ingelogd
      </span>
    );
  }
  if (daysSinceLogin < 7) {
    return (
      <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-700">
        {daysSinceLogin === 0 ? "Vandaag" : `${daysSinceLogin}d geleden`}
      </span>
    );
  }
  if (daysSinceLogin <= 30) {
    return (
      <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-700">
        {daysSinceLogin}d geleden
      </span>
    );
  }
  return (
    <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-700">
      {daysSinceLogin}d geleden
    </span>
  );
}

// ── Main Component ─────────────────────────────────────────────────────────────

export default function PartnerAdminDashboard() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [data, setData] = useState<PartnerAdminDashboardData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [openModal, setOpenModal] = useState<string | null>(null);
  const [chartModal, setChartModal] = useState<{ title: string; params: string } | null>(null);

  // Sort state for HMP table
  const [sortColumn, setSortColumn] = useState<keyof HMPActivity>("name");
  const [sortDir, setSortDir] = useState<"asc" | "desc">("asc");

  useEffect(() => {
    setLoading(true);
    get<PartnerAdminDashboardData>("/dashboard/partner-admin")
      .then(setData)
      .catch((err) => setError(err.message))
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <LoadingState />;
  if (error) return <ErrorState message={error} />;
  if (!data) return null;

  // Sort HMP activity
  const sortedHmps = [...data.hmp_activity].sort((a, b) => {
    const va = a[sortColumn] ?? "";
    const vb = b[sortColumn] ?? "";
    const cmp =
      typeof va === "number" && typeof vb === "number"
        ? va - vb
        : String(va).localeCompare(String(vb), "nl");
    return sortDir === "asc" ? cmp : -cmp;
  });

  function toggleSort(col: keyof HMPActivity) {
    if (sortColumn === col) {
      setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    } else {
      setSortColumn(col);
      setSortDir("asc");
    }
  }

  function SortIcon({ col }: { col: keyof HMPActivity }) {
    if (sortColumn !== col) return <span className="opacity-30 ml-1">↕</span>;
    return <span className="ml-1">{sortDir === "asc" ? "↑" : "↓"}</span>;
  }

  const thClass =
    "py-2.5 px-3 text-xs font-semibold text-slate-500 uppercase tracking-wide text-left cursor-pointer select-none hover:text-slate-700 whitespace-nowrap";

  // Bar chart data for vestiging
  const vestigingChartData = data.per_vestiging.map((v) => ({
    name: v.tab_name,
    open: v.open,
    hoog: v.hoog,
    in_bemiddeling: v.in_bemiddeling,
  }));

  const partnerId = user?.partner_id;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h2 className="text-xl font-bold text-slate-800">
          Activiteit overzicht — {data.partner_name}
        </h2>
        <p className="text-sm text-slate-500 mt-1">
          Inzicht in hoe jouw hiring managers het portaal gebruiken
        </p>
      </div>

      {/* KPI row */}
      <div className="grid grid-cols-2 sm:grid-cols-3 xl:grid-cols-5 gap-4">
        <ClickableKPICard onClick={() => setOpenModal("open")}>
          <KPICard
            icon={Briefcase}
            label="Open Vacatures"
            value={data.open_vacatures}
            color="#65ADDD"
          />
        </ClickableKPICard>
        <ClickableKPICard onClick={() => setOpenModal("hoog")}>
          <KPICard
            icon={AlertTriangle}
            label="Hoge Prioriteit"
            value={data.hoge_prioriteit}
            color="#EF4444"
          />
        </ClickableKPICard>
        <ClickableKPICard onClick={() => setOpenModal("in_bemiddeling")}>
          <KPICard
            icon={Users}
            label="In Bemiddeling"
            value={data.in_bemiddeling}
            color="#65ADDD"
          />
        </ClickableKPICard>
        <ClickableKPICard onClick={() => setOpenModal("geplaatst")}>
          <KPICard
            icon={TrendingUp}
            label="Geplaatst"
            value={data.geplaatst}
            color="#10B981"
          />
        </ClickableKPICard>
        {/* Totaal — read-only, no modal */}
        <div>
          <KPICard
            icon={CheckCircle}
            label="Totaal Vacatures"
            value={data.totaal_vacatures}
            color="#94A3B8"
          />
        </div>
      </div>

      {/* HMP Activiteit tabel */}
      <div className="card p-4 md:p-6">
        <div className="flex items-center gap-2 mb-4">
          <Activity size={18} className="text-waves-blue" />
          <h3 className="font-semibold text-slate-700">HMP Activiteit</h3>
        </div>

        {sortedHmps.length === 0 ? (
          <p className="text-slate-400 text-sm">Geen hiring managers gevonden.</p>
        ) : (
          <>
            {/* Desktop table */}
            <div className="hidden md:block overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-slate-100">
                    <th className={thClass} onClick={() => toggleSort("name")}>
                      HMP <SortIcon col="name" />
                    </th>
                    <th className={thClass} onClick={() => toggleSort("days_since_login")}>
                      Laatst ingelogd <SortIcon col="days_since_login" />
                    </th>
                    <th
                      className={thClass}
                      onClick={() => toggleSort("vacatures_added_30d")}
                    >
                      Toegevoegd (30d) <SortIcon col="vacatures_added_30d" />
                    </th>
                    <th
                      className={thClass}
                      onClick={() => toggleSort("vacatures_updated_30d")}
                    >
                      Wijzigingen (30d) <SortIcon col="vacatures_updated_30d" />
                    </th>
                    <th
                      className={thClass}
                      onClick={() => toggleSort("total_vacatures")}
                    >
                      Open vacatures <SortIcon col="total_vacatures" />
                    </th>
                    <th
                      className={thClass}
                      onClick={() => toggleSort("stale_vacatures")}
                    >
                      Verlopen review <SortIcon col="stale_vacatures" />
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {sortedHmps.map((hmp) => (
                    <tr
                      key={hmp.id}
                      className="border-b border-slate-50 hover:bg-slate-50 transition-colors"
                    >
                      <td className="py-3 px-3">
                        <div className="flex items-center gap-2">
                          <div
                            className="w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold text-white flex-shrink-0"
                            style={{ backgroundColor: "#65ADDD" }}
                          >
                            {hmp.name.charAt(0).toUpperCase()}
                          </div>
                          <div className="min-w-0">
                            <div className="font-medium text-slate-800 truncate">
                              {hmp.name}
                            </div>
                            <div className="text-xs text-slate-400 truncate">
                              {hmp.email}
                            </div>
                          </div>
                        </div>
                      </td>
                      <td className="py-3 px-3">
                        <LoginBadge daysSinceLogin={hmp.days_since_login} />
                      </td>
                      <td className="py-3 px-3 text-slate-700 font-medium">
                        {hmp.vacatures_added_30d}
                      </td>
                      <td className="py-3 px-3 text-slate-700 font-medium">
                        {hmp.vacatures_updated_30d}
                      </td>
                      <td className="py-3 px-3 text-slate-700 font-medium">
                        {hmp.total_vacatures}
                      </td>
                      <td className="py-3 px-3">
                        {hmp.stale_vacatures > 0 ? (
                          <span className="inline-flex items-center gap-1 text-red-600 font-semibold">
                            <AlertTriangle size={14} className="flex-shrink-0" />
                            {hmp.stale_vacatures}
                          </span>
                        ) : (
                          <span className="text-slate-400">—</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Mobile card view */}
            <div className="block md:hidden space-y-3">
              {sortedHmps.map((hmp) => (
                <div
                  key={hmp.id}
                  className="border border-slate-100 rounded-xl p-4 space-y-3"
                >
                  <div className="flex items-center justify-between gap-2">
                    <div className="flex items-center gap-2 min-w-0">
                      <div
                        className="w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold text-white flex-shrink-0"
                        style={{ backgroundColor: "#65ADDD" }}
                      >
                        {hmp.name.charAt(0).toUpperCase()}
                      </div>
                      <div className="min-w-0">
                        <div className="font-medium text-slate-800 truncate">
                          {hmp.name}
                        </div>
                        <div className="text-xs text-slate-400 truncate">
                          {hmp.email}
                        </div>
                      </div>
                    </div>
                    <LoginBadge daysSinceLogin={hmp.days_since_login} />
                  </div>
                  <div className="grid grid-cols-3 gap-3 text-center">
                    <div className="bg-slate-50 rounded-lg px-2 py-2">
                      <div className="text-lg font-bold text-slate-700">
                        {hmp.vacatures_added_30d}
                      </div>
                      <div className="text-xs text-slate-400">Toegevoegd</div>
                    </div>
                    <div className="bg-slate-50 rounded-lg px-2 py-2">
                      <div className="text-lg font-bold text-slate-700">
                        {hmp.vacatures_updated_30d}
                      </div>
                      <div className="text-xs text-slate-400">Wijzigingen</div>
                    </div>
                    <div className="bg-slate-50 rounded-lg px-2 py-2">
                      <div className="text-lg font-bold text-slate-700">
                        {hmp.total_vacatures}
                      </div>
                      <div className="text-xs text-slate-400">Open</div>
                    </div>
                  </div>
                  {hmp.stale_vacatures > 0 && (
                    <div className="flex items-center gap-1.5 text-red-600 text-sm font-medium">
                      <AlertTriangle size={14} />
                      {hmp.stale_vacatures} verlopen{" "}
                      {hmp.stale_vacatures === 1 ? "review" : "reviews"}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </>
        )}
      </div>

      {/* Charts row */}
      {vestigingChartData.length > 0 && (
        <div className="card p-4 md:p-6">
          <h3 className="font-semibold text-slate-700 mb-4">Open Vacatures per Vestiging</h3>
          <div className="overflow-x-auto">
            <div style={{ minWidth: 280 }}>
              <ResponsiveContainer width="100%" height={220}>
                <BarChart data={vestigingChartData} barGap={4}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#F1F5F9" />
                  <XAxis
                    dataKey="name"
                    tick={{ fontSize: 11, fill: "#64748B" }}
                    axisLine={false}
                    tickLine={false}
                  />
                  <YAxis
                    tick={{ fontSize: 11, fill: "#64748B" }}
                    axisLine={false}
                    tickLine={false}
                    allowDecimals={false}
                  />
                  <Tooltip
                    contentStyle={{
                      borderRadius: 8,
                      border: "1px solid #E2E8F0",
                      boxShadow: "0 4px 6px -1px rgba(0,0,0,0.08)",
                      fontSize: 13,
                    }}
                  />
                  <Bar
                    dataKey="open"
                    name="Open"
                    fill="#65ADDD"
                    radius={[4, 4, 0, 0]}
                    cursor="pointer"
                    onClick={(barData: any) => {
                      const tabName = barData?.name;
                      if (tabName) {
                        setChartModal({
                          title: `Open vacatures — ${tabName}`,
                          params: `status=open&locatie=${encodeURIComponent(tabName)}`,
                        });
                      }
                    }}
                  />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>
        </div>
      )}

      {/* Recent activity */}
      <div className="card p-4 md:p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-semibold text-slate-700">Recente Activiteit</h3>
          <button
            onClick={() => navigate("/audit-trail")}
            className="flex items-center gap-1 text-sm text-waves-blue hover:text-waves-blue-hover font-medium transition-colors"
          >
            Bekijk alles
            <ChevronRight size={14} />
          </button>
        </div>
        {data.recent_activity.length === 0 ? (
          <p className="text-slate-400 text-sm">Geen recente activiteit.</p>
        ) : (
          <div className="space-y-1">
            {data.recent_activity.slice(0, 8).map((entry) => (
              <div
                key={entry.id}
                className="flex flex-wrap md:flex-nowrap items-start md:items-center gap-2 md:gap-4 py-2.5 border-b border-slate-50 last:border-0 text-sm"
              >
                <span className="text-slate-400 text-xs w-full md:w-32 md:flex-shrink-0">
                  {new Date(entry.created_at).toLocaleString("nl-NL", {
                    day: "2-digit",
                    month: "short",
                    hour: "2-digit",
                    minute: "2-digit",
                  })}
                </span>
                {actionBadge(entry.action)}
                <span className="text-slate-700 font-medium truncate">
                  {entry.vacature_functie ?? "—"}
                </span>
                <span className="text-slate-400 truncate hidden md:block">
                  {entry.partner_name ?? "—"}
                </span>
                <span className="md:ml-auto text-slate-500 text-xs truncate">
                  {entry.user_name}
                </span>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* KPI Modals */}
      {openModal === "open" && (
        <VacatureListModal
          title="Open Vacatures"
          filterParams="status=open"
          myHmps={false}
          onClose={() => setOpenModal(null)}
        />
      )}
      {openModal === "hoog" && (
        <VacatureListModal
          title="Hoge Prioriteit"
          filterParams="status=open&priority=hoog"
          myHmps={false}
          onClose={() => setOpenModal(null)}
        />
      )}
      {openModal === "in_bemiddeling" && (
        <VacatureListModal
          title="In Bemiddeling"
          filterParams="bemiddeling_status=in_bemiddeling"
          myHmps={false}
          onClose={() => setOpenModal(null)}
        />
      )}
      {openModal === "geplaatst" && (
        <VacatureListModal
          title="Geplaatst"
          filterParams="bemiddeling_status=geplaatst"
          myHmps={false}
          onClose={() => setOpenModal(null)}
        />
      )}

      {/* Chart click modal */}
      {chartModal && (
        <VacatureListModal
          title={chartModal.title}
          filterParams={chartModal.params}
          myHmps={false}
          onClose={() => setChartModal(null)}
        />
      )}
    </div>
  );
}
